// A small card for a member, opened by clicking them in the member list: who they are, and "Message" to DM them.
// A popover beside the list on desktop, a bottom sheet on phones.
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { accessLabel, type BotInfo, type Member } from "@bloom/shared";
import { isNarrow } from "../mobile.ts";
import { Avatar } from "./ui.tsx";
import { Sheet } from "./Sheet.tsx";
import "./dms.css";

export function MemberMenu({ member, anchor, online, isMe, onMessage, onClose }: {
  member: Member;
  /** The row that was clicked, to sit next to (desktop). */
  anchor: DOMRect;
  online: boolean;
  isMe: boolean;
  onMessage: () => void;
  onClose: () => void;
}) {
  const sheet = isNarrow();
  const card = useRef<HTMLDivElement>(null);

  // Desktop: a click anywhere else or Esc closes it.
  useEffect(() => {
    if (sheet) return;
    const onDown = (e: MouseEvent) => !card.current?.contains(e.target as Node) && onClose();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [sheet]);

  const body = (
    <div className="member-card-body">
      <div className="member-card-who">
        <Avatar id={member.id} name={member.name} size={40} presence={online ? "online" : undefined} />
        <div style={{ minWidth: 0 }}>
          <div className="member-card-name">{member.name}{isMe && <span className="muted" style={{ fontWeight: 400 }}> (you)</span>}</div>
          <div className="muted" style={{ fontSize: 12 }}>{online ? "Online" : "Offline"}{member.role !== "member" ? ` · ${member.role[0]!.toUpperCase()}${member.role.slice(1)}` : ""}</div>
        </div>
      </div>
      {!isMe && (
        <button className="btn btn-primary btn-small member-card-message" onClick={onMessage} autoFocus={!sheet}>
          Message
        </button>
      )}
    </div>
  );

  if (sheet) return <Sheet label={member.name} onClose={onClose}>{body}</Sheet>;
  // Left of the member list, lined up with the row, kept on screen.
  const top = Math.max(8, Math.min(anchor.top - 8, window.innerHeight - 140));
  return createPortal(
    <div ref={card} className="member-card" role="dialog" aria-label={member.name} style={{ top, right: window.innerWidth - anchor.left + 8 }}>
      {body}
    </div>,
    document.body,
  );
}

/**
 * The same card for a bot (docs/bots.md): who made it, who it talks to, "Message" to DM it (when it talks to me) and
 * "Open" for its page in the app home.
 */
export function BotMenu({ bot, ownerName, anchor, canMessage, onMessage, onOpen, onClose }: {
  bot: BotInfo;
  ownerName: string;
  anchor: DOMRect;
  canMessage: boolean;
  onMessage: () => void;
  onOpen: () => void;
  onClose: () => void;
}) {
  const sheet = isNarrow();
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (sheet) return;
    const onDown = (e: MouseEvent) => !card.current?.contains(e.target as Node) && onClose();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [sheet]);
  const body = (
    <div className="member-card-body">
      <div className="member-card-who">
        <Avatar id={bot.id} name={bot.name} size={40} />
        <div style={{ minWidth: 0 }}>
          <div className="member-card-name">
            {bot.name} <span className="ai-tag bot-tag">BOT</span>
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            By {ownerName} · {bot.disabled ? "Switched off" : `Talks to ${accessLabel(bot.access, ownerName).replace(/^Only /, "only ").replace(/^Everyone$/, "everyone")}`}
          </div>
        </div>
      </div>
      <div className="member-card-actions">
        {canMessage && (
          <button className="btn btn-primary btn-small member-card-message" onClick={onMessage} autoFocus={!sheet}>
            Message
          </button>
        )}
        <button className="btn btn-small member-card-message" onClick={onOpen}>
          About
        </button>
      </div>
    </div>
  );
  if (sheet) return <Sheet label={bot.name} onClose={onClose}>{body}</Sheet>;
  const top = Math.max(8, Math.min(anchor.top - 8, window.innerHeight - 160));
  return createPortal(
    <div ref={card} className="member-card" role="dialog" aria-label={bot.name} style={{ top, right: window.innerWidth - anchor.left + 8 }}>
      {body}
    </div>,
    document.body,
  );
}
